import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  ConnectionState,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  facingModeFromLocalTrack,
  type RemoteTrack,
} from "livekit-client";
import {
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  SmilePlus,
  SwitchCamera,
  Video,
  VideoOff,
} from "lucide-react";
import { ChatLogo } from "@/components/ChatLogo";
import {
  joinChatGuest,
  leaveChatGuest,
  lobbyChatGuest,
  previewChatGuest,
  touchChatGuest,
  type ChatGuestPreview,
} from "@/lib/guestsApi";

const CALL_EMOJI_TOPIC = "aurelia-call-emoji";
const CALL_REACTION_EMOJIS = ["❤️", "😂", "👍", "🔥", "👏", "🎉", "😍", "😮"] as const;

type Facing = "user" | "environment";

type FloatingEmoji = {
  id: string;
  emoji: string;
  leftPct: number;
  driftPx: number;
  delayMs: number;
  scale: number;
  durationMs: number;
};

function parseIsoMs(value?: string | null): number | null {
  if (!value) return null;
  const raw = value.trim();
  if (!raw) return null;
  const t = Date.parse(raw);
  if (Number.isFinite(t)) return t;
  const withT = Date.parse(raw.replace(" ", "T"));
  return Number.isFinite(withT) ? withT : null;
}

function parseWhenLabelMs(label?: string | null): number | null {
  if (!label) return null;
  const m = label.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+à\s+(\d{1,2}):(\d{2})/u);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4]), Number(m[5]), 0);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function sessionStartMs(preview: ChatGuestPreview | null): number | null {
  const unix = preview?.session_at;
  if (typeof unix === "number" && Number.isFinite(unix) && unix > 1_000_000_000) {
    return unix < 1_000_000_000_000 ? unix * 1000 : unix;
  }
  if (typeof unix === "string" && /^\d{10,13}$/.test(unix)) {
    const n = Number(unix);
    return n < 1_000_000_000_000 ? n * 1000 : n;
  }
  return parseIsoMs(preview?.starts_at) ?? parseWhenLabelMs(preview?.when_label);
}

function formatSessionWhen(ms: number): { day: string; time: string } {
  const d = new Date(ms);
  return {
    day: d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }),
    time: d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
  };
}

function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} j`);
  if (hours > 0) parts.push(`${hours} h`);
  if (mins > 0 || hours > 0 || days > 0) parts.push(`${mins} min`);
  parts.push(`${secs} s`);
  return parts.join(" ");
}

function stopMediaStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  });
}

function stateBanner(
  state: string | undefined,
  opts: { whenLabel?: string; earlyMinutes?: number; inLobby?: boolean }
): { className: string; text: string } {
  const early = opts.earlyMinutes && opts.earlyMinutes > 0 ? opts.earlyMinutes : 15;
  if (!state) {
    return {
      className:
        "rounded-xl border border-[var(--chat-border)] bg-[var(--chat-surface)] px-3 py-2.5 text-center text-sm text-[var(--chat-muted)]",
      text: "Vérification du lien…",
    };
  }
  if (state === "joinable") {
    return {
      className:
        "rounded-xl border border-emerald-500/35 bg-emerald-500/15 px-3 py-2.5 text-center text-sm font-semibold text-emerald-800 dark:text-emerald-200",
      text: "C’est parti — vous pouvez rejoindre",
    };
  }
  if (state === "waiting_host") {
    return {
      className:
        "rounded-xl border border-amber-500/40 bg-amber-500/15 px-3 py-2.5 text-center text-sm font-semibold text-amber-900 dark:text-amber-100",
      text: opts.inLobby
        ? "Vous êtes dans la salle d’attente — dès que l’hôte lance, vous entrez"
        : "Salle d’attente — l’hôte n’a pas encore lancé",
    };
  }
  if (state === "too_early") {
    return {
      className:
        "rounded-xl border border-amber-500/40 bg-amber-500/15 px-3 py-2.5 text-center text-sm font-semibold text-amber-900 dark:text-amber-100",
      text: opts.whenLabel
        ? `Salle d’attente ${early} min avant (${opts.whenLabel})`
        : `Salle d’attente ${early} min avant l’heure`,
    };
  }
  if (state === "ended") {
    return {
      className:
        "rounded-xl border border-red-500/35 bg-red-500/10 px-3 py-2.5 text-center text-sm font-semibold text-red-800 dark:text-red-200",
      text: "Cette session a expiré",
    };
  }
  if (state === "revoked") {
    return {
      className:
        "rounded-xl border border-red-500/35 bg-red-500/10 px-3 py-2.5 text-center text-sm font-semibold text-red-800 dark:text-red-200",
      text: "Cette invitation a été retirée",
    };
  }
  return {
    className:
      "rounded-xl border border-[var(--chat-border)] bg-[var(--chat-surface)] px-3 py-2.5 text-center text-sm text-[var(--chat-muted)]",
    text: "Lien d’invitation",
  };
}

function guessFacingFromLabel(label: string): Facing | null {
  const l = label.toLowerCase();
  if (/back|rear|environment|arrière|arriere|world|posterior/i.test(l)) return "environment";
  if (/front|user|face|avant|selfie/i.test(l)) return "user";
  return null;
}

async function listVideoInputs(): Promise<MediaDeviceInfo[]> {
  try {
    return await Room.getLocalDevices("videoinput", true);
  } catch {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      return list.filter((d) => d.kind === "videoinput");
    } catch {
      return [];
    }
  }
}

function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
}

export function ChatGuestJoin({ token }: { token: string }) {
  const [preview, setPreview] = useState<ChatGuestPreview | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inCall, setInCall] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [status, setStatus] = useState("Connexion…");
  const [canFlipCam, setCanFlipCam] = useState(false);
  const [flipBusy, setFlipBusy] = useState(false);
  const [facing, setFacing] = useState<Facing>("user");
  const [showReactions, setShowReactions] = useState(false);
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  const [connected, setConnected] = useState(false);
  const [lobbyReady, setLobbyReady] = useState(false);
  const [lobbyBusy, setLobbyBusy] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const roomRef = useRef<Room | null>(null);
  const localRef = useRef<HTMLVideoElement | null>(null);
  const remoteRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const lobbyVideoRef = useRef<HTMLVideoElement | null>(null);
  const lobbyStreamRef = useRef<MediaStream | null>(null);
  const inCallRef = useRef(false);
  const facingRef = useRef<Facing>("user");
  const spawnEmojiRef = useRef<(emoji: string) => void>(() => undefined);
  const clockOffsetRef = useRef(0);
  const startRef = useRef<() => Promise<void>>(async () => undefined);
  const lobbyMicOnRef = useRef(true);
  const lobbyCamOnRef = useRef(true);
  inCallRef.current = inCall;

  const stopLobbyStream = useCallback(() => {
    stopMediaStream(lobbyStreamRef.current);
    lobbyStreamRef.current = null;
    if (lobbyVideoRef.current) lobbyVideoRef.current.srcObject = null;
  }, []);

  const spawnEmoji = useCallback((emoji: string) => {
    if (!emoji) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const burst = 10 + Math.floor(Math.random() * 5);
    const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const items: FloatingEmoji[] = Array.from({ length: burst }, (_, i) => ({
      id: `${stamp}-${i}`,
      emoji,
      leftPct: 10 + Math.random() * 80,
      driftPx: -48 + Math.random() * 96,
      delayMs: Math.floor(Math.random() * 420),
      scale: 0.75 + Math.random() * 0.7,
      durationMs: 2200 + Math.floor(Math.random() * 900),
    }));
    setFloatingEmojis((prev) => [...prev.slice(-40), ...items]);
    const maxLife = Math.max(...items.map((it) => it.delayMs + it.durationMs)) + 80;
    window.setTimeout(() => {
      setFloatingEmojis((prev) => prev.filter((e) => !e.id.startsWith(stamp)));
    }, maxLife);
  }, []);
  spawnEmojiRef.current = spawnEmoji;

  const sendReaction = useCallback(
    (emoji: string) => {
      spawnEmoji(emoji);
      const room = roomRef.current;
      if (!room || room.state !== ConnectionState.Connected) return;
      try {
        const payload = new TextEncoder().encode(JSON.stringify({ t: "rx", e: emoji }));
        void room.localParticipant.publishData(payload, {
          reliable: false,
          topic: CALL_EMOJI_TOPIC,
        });
      } catch {
        /* ignore */
      }
    },
    [spawnEmoji]
  );

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (inCallRef.current) return;
      const r = await previewChatGuest(token);
      if (cancelled) return;
      if (!r.success || !r.data) {
        setError(r.message || "Lien invalide.");
        return;
      }
      const serverNow = parseIsoMs(r.data.server_now);
      if (serverNow != null) clockOffsetRef.current = serverNow - Date.now();
      setPreview(r.data);
      const suggested = r.data.suggested_name || "";
      setName((prev) => prev || suggested);
    };
    void load();
    const id = window.setInterval(() => void load(), lobbyReady ? 1200 : 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token, lobbyReady]);

  useEffect(() => {
    if (!inCall) return;
    const id = window.setInterval(() => {
      void touchChatGuest(token).then((r) => {
        if (!r.success && (r.error === "revoked" || r.error === "ended")) {
          void hangup();
        }
      });
    }, 12000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCall, token]);

  useEffect(() => {
    if (preview?.state === "ended" || preview?.state === "revoked") {
      stopLobbyStream();
      setLobbyReady(false);
    }
  }, [preview?.state, stopLobbyStream]);

  useEffect(() => () => stopLobbyStream(), [stopLobbyStream]);

  const hangup = async () => {
    const room = roomRef.current;
    roomRef.current = null;
    try {
      await room?.disconnect();
    } catch {
      /* ignore */
    }
    void leaveChatGuest(token);
    stopLobbyStream();
    setLobbyReady(false);
    setInCall(false);
    setConnected(false);
    setStatus("Visio terminée");
  };

  const refreshCameras = async () => {
    const devices = await listVideoInputs();
    setCanFlipCam(devices.length > 1 || isMobileViewport());
  };

  const start = async () => {
    if (busy) return;
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError("Indiquez votre prénom (2 caractères min.).");
      return;
    }
    setBusy(true);
    setError(null);
    const r = await joinChatGuest(token, trimmed);
    if (!r.success || !r.data) {
      setBusy(false);
      setError(r.message || "Impossible de rejoindre.");
      if (r.error === "waiting_host" || r.error === "too_early") {
        const again = await previewChatGuest(token);
        if (again.success && again.data) setPreview(again.data);
      }
      return;
    }
    const lobby = lobbyStreamRef.current;
    const reuseVideo =
      lobbyCamOnRef.current
        ? lobby?.getVideoTracks().find((t) => t.readyState === "live") ?? null
        : null;
    const reuseAudio =
      lobbyMicOnRef.current
        ? lobby?.getAudioTracks().find((t) => t.readyState === "live") ?? null
        : null;
    if (lobbyVideoRef.current) lobbyVideoRef.current.srcObject = null;
    lobbyStreamRef.current = null;
    lobby?.getTracks().forEach((t) => {
      if (t !== reuseVideo && t !== reuseAudio) {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      }
    });

    setInCall(true);
    setStatus("Connexion…");
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;
    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Video && remoteRef.current) {
        track.attach(remoteRef.current);
      }
      if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
        track.attach(remoteAudioRef.current);
      }
    });
    room.on(RoomEvent.DataReceived, (payload, _p, _k, topic) => {
      if (topic && topic !== CALL_EMOJI_TOPIC) return;
      try {
        const raw = new TextDecoder().decode(payload);
        const msg = JSON.parse(raw) as { t?: string; e?: string };
        if (msg?.t === "rx" && typeof msg.e === "string" && msg.e.length <= 8) {
          spawnEmojiRef.current(msg.e);
        }
      } catch {
        /* ignore */
      }
    });
    try {
      await room.connect(r.data.livekit_url, r.data.token);
      await new Promise((res) => window.setTimeout(res, 400));
      if (room.state !== ConnectionState.Connected) {
        throw new Error("Connexion visio interrompue.");
      }
      if (reuseAudio) {
        await room.localParticipant.publishTrack(reuseAudio, { source: Track.Source.Microphone });
      } else if (lobbyMicOnRef.current) {
        await room.localParticipant.setMicrophoneEnabled(true);
      } else {
        setMicOn(false);
      }
      try {
        if (reuseVideo) {
          await room.localParticipant.publishTrack(reuseVideo, { source: Track.Source.Camera });
        } else if (lobbyCamOnRef.current) {
          await room.localParticipant.setCameraEnabled(true);
        } else {
          setCamOn(false);
        }
      } catch (camErr) {
        console.warn("[chat-guest] camera", camErr);
        setCamOn(false);
        setError("Caméra indisponible — vous restez en visio (micro).");
        try {
          reuseVideo?.stop();
        } catch {
          /* ignore */
        }
      }
      const cam = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (cam?.track && localRef.current) cam.track.attach(localRef.current);
      setConnected(true);
      setStatus("Connecté");
      void refreshCameras();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connexion visio impossible.");
      await hangup();
    } finally {
      setBusy(false);
    }
  };
  startRef.current = start;

  useEffect(() => {
    if (inCall || busy || !lobbyReady) return;
    if (preview?.state !== "joinable") return;
    if (name.trim().length < 2) return;
    void startRef.current();
  }, [preview?.state, lobbyReady, inCall, busy, name]);

  const enterLobby = async () => {
    if (lobbyBusy || lobbyReady) return;
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError("Indiquez votre prénom (2 caractères min.).");
      return;
    }
    setLobbyBusy(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: { facingMode: "user" },
      });
      stopLobbyStream();
      lobbyStreamRef.current = stream;
      if (lobbyVideoRef.current) {
        lobbyVideoRef.current.srcObject = stream;
      }
      lobbyMicOnRef.current = true;
      lobbyCamOnRef.current = true;
      setMicOn(true);
      setCamOn(true);
      setLobbyReady(true);
      void lobbyChatGuest(token, trimmed);
    } catch {
      setError("Caméra ou micro refusé — vous pourrez les activer en rejoignant.");
      setLobbyReady(true);
      void lobbyChatGuest(token, trimmed);
    } finally {
      setLobbyBusy(false);
    }
  };

  const toggleLobbyMic = () => {
    const next = !micOn;
    lobbyStreamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = next;
    });
    lobbyMicOnRef.current = next;
    setMicOn(next);
  };

  const toggleLobbyCam = () => {
    const next = !camOn;
    lobbyStreamRef.current?.getVideoTracks().forEach((t) => {
      t.enabled = next;
    });
    lobbyCamOnRef.current = next;
    setCamOn(next);
  };

  const toggleMic = async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !micOn;
    await room.localParticipant.setMicrophoneEnabled(next);
    setMicOn(next);
  };

  const toggleCam = async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !camOn;
    await room.localParticipant.setCameraEnabled(next);
    setCamOn(next);
    if (next && localRef.current) {
      const cam = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (cam?.track) cam.track.attach(localRef.current);
    }
  };

  const flipCamera = async () => {
    if (flipBusy || !camOn) return;
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track;
    if (!(track instanceof LocalVideoTrack)) return;
    setFlipBusy(true);
    try {
      const devices = await listVideoInputs();
      setCanFlipCam(devices.length > 1 || isMobileViewport());
      const currentId = track.mediaStreamTrack.getSettings().deviceId ?? "";
      const currentIdx = devices.findIndex((d) => d.deviceId === currentId);
      const wantFacing: Facing = facingRef.current === "user" ? "environment" : "user";
      let nextDevice: MediaDeviceInfo | undefined;
      if (devices.length >= 2) {
        nextDevice = devices.find((d, i) => i !== currentIdx && guessFacingFromLabel(d.label) === wantFacing);
        if (!nextDevice) {
          nextDevice = devices[(Math.max(currentIdx, 0) + 1) % devices.length];
        }
      }
      if (nextDevice?.deviceId && nextDevice.deviceId !== currentId) {
        await room.switchActiveDevice("videoinput", nextDevice.deviceId, true);
      } else {
        await track.restartTrack({ facingMode: wantFacing });
      }
      const live = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (live instanceof LocalVideoTrack && localRef.current) {
        live.attach(localRef.current);
        const detected = facingModeFromLocalTrack(live).facingMode;
        const nextFacing: Facing =
          detected === "environment"
            ? "environment"
            : guessFacingFromLabel(live.mediaStreamTrack.label) === "environment"
              ? "environment"
              : "user";
        facingRef.current = nextFacing;
        setFacing(nextFacing);
      } else {
        facingRef.current = wantFacing;
        setFacing(wantFacing);
      }
    } catch {
      setError("Impossible de changer de caméra.");
    } finally {
      setFlipBusy(false);
    }
  };

  const joinable = preview?.state === "joinable";
  const canEnterLobby = preview?.state === "waiting_host" || preview?.state === "joinable";
  const locked = preview?.state === "ended" || preview?.state === "revoked" || preview?.state === "too_early";
  const hostLabel = preview?.host_name || "Hôte";
  const banner = stateBanner(preview?.state, {
    whenLabel: preview?.when_label,
    earlyMinutes: preview?.early_minutes,
    inLobby: lobbyReady,
  });

  const syncedNow = nowMs + clockOffsetRef.current;
  const startsAtMs = sessionStartMs(preview);
  const opensAtMs =
    parseIsoMs(preview?.opens_at) ??
    (startsAtMs != null ? startsAtMs - (preview?.early_minutes ?? 15) * 60_000 : null);
  const remainingToStart = startsAtMs != null ? startsAtMs - syncedNow : null;
  const remainingToOpen = opensAtMs != null ? opensAtMs - syncedNow : null;
  const showStartCountdown =
    remainingToStart != null &&
    remainingToStart > 0 &&
    preview?.state !== "ended" &&
    preview?.state !== "revoked";
  const showOpenHint =
    preview?.state === "too_early" && remainingToOpen != null && remainingToOpen > 0;

  const attachLobbyVideo = (el: HTMLVideoElement | null) => {
    lobbyVideoRef.current = el;
    if (el && lobbyStreamRef.current) el.srcObject = lobbyStreamRef.current;
  };

  const stage = (
    <div
      className="chat-call-overlay fixed inset-0 z-20 flex flex-col overflow-hidden bg-[#070b14] text-white"
      role="dialog"
      aria-label={`Visio avec ${hostLabel}`}
    >
      <div className="pointer-events-none absolute inset-0 z-[15] overflow-hidden" aria-hidden>
        {floatingEmojis.map((item) => (
          <span
            key={item.id}
            className="chat-call-emoji-float"
            style={
              {
                left: `${item.leftPct}%`,
                animationDelay: `${item.delayMs}ms`,
                animationDuration: `${item.durationMs}ms`,
                ["--chat-emoji-drift" as string]: `${item.driftPx}px`,
                ["--chat-emoji-scale" as string]: String(item.scale),
              } as CSSProperties
            }
          >
            {item.emoji}
          </span>
        ))}
      </div>

      <div className="chat-call-header relative z-10 flex items-center justify-between gap-3 px-4 pb-5 pt-3.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <ChatLogo size={28} />
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold tracking-tight">{hostLabel}</p>
            <div
              className={`chat-call-status-chip mt-1 ${connected ? "" : "chat-call-status-chip--ringing"}`}
            >
              {status}
            </div>
          </div>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 bg-black">
        <video
          ref={remoteRef}
          className="absolute inset-0 h-full w-full bg-black object-contain"
          playsInline
          autoPlay
        />
        <video
          ref={localRef}
          className="chat-call-thumb absolute bottom-3 right-3 z-[5] h-28 w-20 rounded-lg object-cover ring-1 ring-white/25 md:bottom-4 md:right-4 md:h-36 md:w-28"
          playsInline
          autoPlay
          muted
        />
        <audio ref={remoteAudioRef} autoPlay />
      </div>

      <div className="chat-call-dock relative z-10 flex shrink-0 flex-col items-center gap-2.5 px-3 pb-4 pt-1">
        {showReactions ? (
          <div className="chat-call-reactions flex max-w-full items-center gap-1 overflow-x-auto px-2 py-1.5">
            {CALL_REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="flex size-10 shrink-0 items-center justify-center rounded-full text-xl transition hover:bg-white/15 active:scale-90"
                onClick={() => {
                  sendReaction(emoji);
                  setShowReactions(false);
                }}
                aria-label={`Envoyer ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
        <div className="chat-call-controls">
          <button
            type="button"
            className={`chat-call-ctrl ${!micOn ? "chat-call-ctrl--off" : ""}`}
            onClick={() => void toggleMic()}
            aria-label={micOn ? "Couper le micro" : "Activer le micro"}
            disabled={!connected}
          >
            {micOn ? <Mic className="size-5" /> : <MicOff className="size-5" />}
          </button>
          <button
            type="button"
            className={`chat-call-ctrl ${showReactions ? "chat-call-ctrl--active" : ""}`}
            onClick={() => setShowReactions((v) => !v)}
            aria-label="Réactions emoji"
            disabled={!connected}
          >
            <SmilePlus className="size-5" />
          </button>
          <button
            type="button"
            className={`chat-call-ctrl ${!camOn ? "chat-call-ctrl--off" : ""}`}
            onClick={() => void toggleCam()}
            aria-label={camOn ? "Couper la caméra" : "Activer la caméra"}
            disabled={!connected}
          >
            {camOn ? <Video className="size-5" /> : <VideoOff className="size-5" />}
          </button>
          {canFlipCam && camOn && connected ? (
            <button
              type="button"
              className="chat-call-ctrl"
              onClick={() => void flipCamera()}
              disabled={flipBusy}
              aria-label={facing === "user" ? "Caméra arrière" : "Caméra avant"}
              title="Changer de caméra"
            >
              <SwitchCamera className={`size-5 ${flipBusy ? "animate-spin" : ""}`} />
            </button>
          ) : null}
          <button
            type="button"
            className="chat-call-ctrl chat-call-ctrl--hangup"
            onClick={() => void hangup()}
            aria-label="Raccrocher"
          >
            <PhoneOff className="size-6" />
          </button>
        </div>
      </div>
    </div>
  );

  const primaryLabel = (() => {
    if (busy) return "Connexion…";
    if (joinable) return "Rejoindre la visio";
    if (lobbyReady) return "En salle d’attente…";
    if (canEnterLobby) return "Entrer dans la salle d’attente";
    return "Rejoindre la visio";
  })();

  const primaryDisabled =
    busy ||
    lobbyBusy ||
    locked ||
    !preview ||
    (lobbyReady && !joinable) ||
    (!canEnterLobby && !joinable);

  const onPrimary = () => {
    if (joinable) {
      void start();
      return;
    }
    if (canEnterLobby && !lobbyReady) {
      void enterLobby();
    }
  };

  const when = startsAtMs != null ? formatSessionWhen(startsAtMs) : null;
  const showWhenCard = Boolean(when || (preview?.when_label && preview.when_label.includes(" à ")));
  const showStatusChip =
    preview?.state === "ended" ||
    preview?.state === "revoked" ||
    preview?.state === "joinable" ||
    (preview?.state === "waiting_host" && lobbyReady);

  return (
    <div className="chat-app flex min-h-dvh flex-col bg-[var(--chat-bg)] text-[var(--chat-text)]">
      {!inCall ? (
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center gap-7 px-6 py-12">
          <div className="flex flex-col items-center gap-5 text-center">
            <ChatLogo size={56} />
            <div className="space-y-1.5">
              <p className="text-sm text-white/50">Bienvenue dans Aurélia Chat</p>
              <h1 className="text-[1.75rem] font-semibold leading-tight tracking-tight">
                {preview?.title || "Réunion visio"}
              </h1>
              <p className="text-sm text-white/50">
                {preview?.host_name ? `${preview.host_name} vous invite` : "Invitation visio"}
              </p>
            </div>
          </div>

          {showWhenCard ? (
            <div className="rounded-3xl border border-white/15 bg-[#1e293b] px-5 py-6 text-center">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/50">
                Début de la session
              </p>
              {when ? (
                <>
                  <p className="mt-3 text-sm capitalize text-white">{when.day}</p>
                  <p className="mt-1 text-5xl font-semibold tabular-nums tracking-tight text-[#5eead4]">
                    {when.time}
                  </p>
                </>
              ) : (
                <p className="mt-3 text-lg font-semibold text-white">{preview?.when_label}</p>
              )}
              {showStartCountdown ? (
                <p className="mt-3 text-base font-medium tabular-nums text-white">
                  dans {formatRemaining(remainingToStart ?? 0)}
                </p>
              ) : null}
              {showOpenHint ? (
                <p className="mt-2 text-xs text-white/55">
                  Salle d’attente dans {formatRemaining(remainingToOpen ?? 0)}
                </p>
              ) : null}
            </div>
          ) : preview?.state === "waiting_host" ? (
            <p className="text-center text-sm text-white/50">Dès que l’hôte lancera la visio</p>
          ) : null}

          {showStatusChip ? <p className={banner.className}>{banner.text}</p> : null}

          {lobbyReady && !locked ? (
            <div className="overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-black">
              <div className="relative aspect-[4/3] bg-[#0b1220]">
                <video
                  ref={attachLobbyVideo}
                  className={`h-full w-full object-cover ${camOn ? "" : "opacity-0"}`}
                  playsInline
                  autoPlay
                  muted
                />
                {!camOn ? (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">
                    Caméra coupée
                  </div>
                ) : null}
              </div>
              <div className="flex items-center justify-center gap-3 bg-[var(--chat-surface)] px-3 py-2.5">
                <button
                  type="button"
                  className={`inline-flex size-10 items-center justify-center rounded-full ${
                    micOn
                      ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
                      : "bg-red-500/15 text-red-700"
                  }`}
                  onClick={toggleLobbyMic}
                  aria-label={micOn ? "Couper le micro" : "Activer le micro"}
                >
                  {micOn ? <Mic className="size-4" /> : <MicOff className="size-4" />}
                </button>
                <button
                  type="button"
                  className={`inline-flex size-10 items-center justify-center rounded-full ${
                    camOn
                      ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
                      : "bg-red-500/15 text-red-700"
                  }`}
                  onClick={toggleLobbyCam}
                  aria-label={camOn ? "Couper la caméra" : "Activer la caméra"}
                >
                  {camOn ? <Video className="size-4" /> : <VideoOff className="size-4" />}
                </button>
              </div>
            </div>
          ) : null}

          <div className="space-y-2.5">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Votre prénom"
              disabled={locked || busy}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm outline-none placeholder:text-white/30 focus:border-[var(--chat-accent)] disabled:opacity-50"
            />
            <button
              type="button"
              disabled={primaryDisabled}
              onClick={onPrimary}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--chat-accent)] py-3 text-sm font-semibold text-[#0f172a] disabled:opacity-40"
            >
              {busy || lobbyBusy ? <Loader2 className="size-4 animate-spin" /> : <Video className="size-4" />}
              {lobbyBusy ? "Préparation…" : primaryLabel}
            </button>
            {error ? <p className="text-center text-xs text-[var(--chat-danger)]">{error}</p> : null}
          </div>
        </div>
      ) : (
        stage
      )}
      {!inCall ? (
        <p className="px-6 pb-6 text-center text-[10px] leading-snug text-white/30">
          © 2026 Aurélia Suite. Tous droits réservés
          <span className="mx-1 opacity-50" aria-hidden>
            •
          </span>
          Conforme RGPD
        </p>
      ) : null}
    </div>
  );
}
